/**
 * AdminCompletenessPage - View content completeness stats
 *
 * Drives:
 * - GET /api/admin/completeness/stats
 * - GET /api/admin/completeness/missing
 */

import { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

interface CompletenessStats {
  media_type: string;
  total_tracked: number;
  missing_imdb_votes: number;
  missing_rt_rating: number;
  missing_imdb_rating: number;
  missing_genre_ids: number;
  _error?: string;
}

interface MissingContentItem {
  media_type: string;
  tmdb_id: number;
  missing_fields: string[];
  has_imdb_id: boolean;
  has_imdb_rating: boolean;
  has_imdb_votes: boolean;
  has_rt_rating: boolean;
  has_metacritic: boolean;
  has_runtime: boolean;
  has_backdrop_path: boolean;
  has_genre_ids: boolean;
  has_ai_facets_v1: boolean;
  has_ai_genre_overlay_v1: boolean;
  last_checked_at: string;
}

interface MissingContentResponse {
  results: MissingContentItem[];
  count: number;
  offset: number;
  limit: number;
}

const MISSING_FIELD_OPTIONS = [
  { value: 'missing_imdb_votes', label: 'IMDb Votes' },
  { value: 'missing_imdb_rating', label: 'IMDb Rating' },
  { value: 'missing_rt_rating', label: 'RT Rating' },
  { value: 'missing_metacritic', label: 'Metacritic' },
  { value: 'missing_runtime', label: 'Runtime' },
  { value: 'missing_backdrop_path', label: 'Backdrop' },
  { value: 'missing_genre_ids', label: 'Genre IDs' },
  { value: 'missing_ai_facets_v1', label: 'AI Facets' },
  { value: 'missing_ai_genre_overlay_v1', label: 'AI Overlay Genres' },
];

export function AdminCompletenessPage() {
  const { session } = useAuth();

  // Stats state
  const [mediaType, setMediaType] = useState<'movie' | 'tv'>('movie');
  const [stats, setStats] = useState<CompletenessStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Missing content state
  const [selectedFields, setSelectedFields] = useState<string[]>(['missing_imdb_votes']);
  const [missingResults, setMissingResults] = useState<MissingContentResponse | null>(null);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingError, setMissingError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Fetch stats
  const fetchStats = async () => {
    if (!session?.access_token) return;

    setStatsLoading(true);
    setStatsError(null);

    try {
      const data = await apiFetch<CompletenessStats>(
        `/admin/completeness/stats?media_type=${mediaType}`,
        { token: session.access_token }
      );
      setStats(data);
    } catch (err) {
      setStatsError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch missing content
  const fetchMissing = async (newOffset = 0) => {
    if (!session?.access_token || selectedFields.length === 0) return;

    setMissingLoading(true);
    setMissingError(null);

    try {
      const fieldsParam = selectedFields.join(',');
      const data = await apiFetch<MissingContentResponse>(
        `/admin/completeness/missing?media_type=${mediaType}&fields=${fieldsParam}&limit=${limit}&offset=${newOffset}`,
        { token: session.access_token }
      );
      setMissingResults(data);
      setOffset(newOffset);
    } catch (err) {
      setMissingError(err instanceof Error ? err.message : 'Failed to fetch missing content');
    } finally {
      setMissingLoading(false);
    }
  };

  // Fetch stats on mount and media type change
  useEffect(() => {
    fetchStats();
  }, [mediaType, session?.access_token]);

  const handleFieldToggle = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  return (
    <AdminLayout
      title="Content Completeness"
      description="View missing data statistics and query content gaps"
    >
      {/* Media Type Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setMediaType('movie')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            mediaType === 'movie'
              ? 'bg-teal-500 text-white'
              : 'bg-[#1A1F26] text-slate-300 hover:text-white'
          }`}
        >
          Movies
        </button>
        <button
          onClick={() => setMediaType('tv')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            mediaType === 'tv'
              ? 'bg-teal-500 text-white'
              : 'bg-[#1A1F26] text-slate-300 hover:text-white'
          }`}
        >
          TV Shows
        </button>
      </div>

      {/* Stats Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Completeness Stats</h3>
          <button
            onClick={fetchStats}
            disabled={statsLoading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
          >
            <RefreshCw size={14} className={statsLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {statsError && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle size={16} />
              <span>{statsError}</span>
            </div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-[#1A1F26] rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Total Tracked</p>
              <p className="text-2xl font-bold text-white">
                {stats.total_tracked.toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-[#1A1F26] rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Missing IMDb Votes</p>
              <p className="text-2xl font-bold text-orange-400">
                {stats.missing_imdb_votes.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">
                {stats.total_tracked > 0
                  ? ((stats.missing_imdb_votes / stats.total_tracked) * 100).toFixed(1)
                  : 0}
                %
              </p>
            </div>
            <div className="p-4 bg-[#1A1F26] rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Missing IMDb Rating</p>
              <p className="text-2xl font-bold text-orange-400">
                {stats.missing_imdb_rating.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">
                {stats.total_tracked > 0
                  ? ((stats.missing_imdb_rating / stats.total_tracked) * 100).toFixed(1)
                  : 0}
                %
              </p>
            </div>
            <div className="p-4 bg-[#1A1F26] rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Missing RT Rating</p>
              <p className="text-2xl font-bold text-orange-400">
                {stats.missing_rt_rating.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">
                {stats.total_tracked > 0
                  ? ((stats.missing_rt_rating / stats.total_tracked) * 100).toFixed(1)
                  : 0}
                %
              </p>
            </div>
            <div className="p-4 bg-[#1A1F26] rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Missing Genre IDs</p>
              <p className="text-2xl font-bold text-orange-400">
                {stats.missing_genre_ids.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">
                {stats.total_tracked > 0
                  ? ((stats.missing_genre_ids / stats.total_tracked) * 100).toFixed(1)
                  : 0}
                %
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Query Missing Content */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Query Missing Content</h3>

        {/* Field Selection */}
        <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08] mb-4">
          <p className="text-sm font-medium text-slate-300 mb-3">
            Select fields to filter by (items must be missing ALL selected fields)
          </p>
          <div className="flex flex-wrap gap-2">
            {MISSING_FIELD_OPTIONS.map((field) => (
              <button
                key={field.value}
                onClick={() => handleFieldToggle(field.value)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  selectedFields.includes(field.value)
                    ? 'bg-teal-500 text-white'
                    : 'bg-[#0F1419] text-slate-400 hover:text-white'
                }`}
              >
                {field.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search Button */}
        <button
          onClick={() => fetchMissing(0)}
          disabled={missingLoading || selectedFields.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
        >
          {missingLoading ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <Search size={16} />
          )}
          Search
        </button>

        {/* Error */}
        {missingError && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle size={16} />
              <span>{missingError}</span>
            </div>
          </div>
        )}

        {/* Results */}
        {missingResults && (
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-400">
                Showing {missingResults.results.length} results (offset: {offset})
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchMissing(Math.max(0, offset - limit))}
                  disabled={offset === 0 || missingLoading}
                  className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => fetchMissing(offset + limit)}
                  disabled={missingResults.results.length < limit || missingLoading}
                  className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {missingResults.results.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                No items found matching the selected criteria
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-white/[0.08]">
                      <th className="pb-2 pr-4">TMDB ID</th>
                      <th className="pb-2 pr-4">Type</th>
                      <th className="pb-2 pr-4">Missing Fields</th>
                      <th className="pb-2">Last Checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingResults.results.map((item) => (
                      <tr
                        key={`${item.media_type}-${item.tmdb_id}`}
                        className="border-b border-white/[0.04]"
                      >
                        <td className="py-2 pr-4">
                          <a
                            href={`/${item.media_type}/${item.tmdb_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-teal-400 hover:text-teal-300"
                          >
                            {item.tmdb_id}
                          </a>
                        </td>
                        <td className="py-2 pr-4 text-slate-300">{item.media_type}</td>
                        <td className="py-2 pr-4">
                          <div className="flex flex-wrap gap-1">
                            {item.missing_fields.slice(0, 3).map((field) => (
                              <span
                                key={field}
                                className="px-2 py-0.5 text-xs bg-orange-500/10 text-orange-400 rounded"
                              >
                                {field.replace('missing_', '')}
                              </span>
                            ))}
                            {item.missing_fields.length > 3 && (
                              <span className="px-2 py-0.5 text-xs text-slate-500">
                                +{item.missing_fields.length - 3} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 text-slate-500">
                          {item.last_checked_at
                            ? new Date(item.last_checked_at).toLocaleDateString()
                            : 'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
